import { useCallback, useEffect, useRef, useState } from "react";
import {
  abrible,
  avisosOmitidos,
  camino,
  contextoDe,
  fechaLlana,
  leerHallazgos,
  lineaDeTiempo,
  loQueDiceElTesoro,
  numeroLlano,
  ROTULOS,
  seAbre,
} from "../../../shared/piezas/index.ts";
import type { Dibujo, Elemento, Fuente, FuenteAbrible, Hallazgos } from "../../../shared/piezas/index.ts";
import { type ActoDetalle, type ClienteNexo, type SujetoFicha, vizHallazgoDetalle, vizHallazgos } from "../api.ts";
import {
  actasDelDocumento,
  conActas,
  esSatRep,
  avisoSatRep,
  FICHA,
  fuenteSinCopia,
  porContraparte,
  subtituloTiempo,
  texto,
  textoMasHuecos,
  textoMasPersonas,
  tonoNivel,
  uuidDe,
  vocesDelCamino,
} from "./catalogo.ts";
import { accionDeError, esCancelacion, mensajeDeError, razonDe } from "./mensajes.ts";
import { pasosQueHacer, textosPrimeraPregunta } from "./partes.ts";
import { AnteElSat } from "./AnteElSat.tsx";
import { Pieza } from "./Pieza.tsx";
import { VerFuente } from "./VerFuente.tsx";
// Instrument Sans desde los WOFF versionados de app/shared/piezas/fuente/, con las mismas declaraciones que Argus Team (un solo
// archivo: los rangos de la calibración de las piezas). El dibujo se pinta con la fuente que se midió.
import "../../../shared/piezas/fuente.css";
import "./ficha.css";

/**
 * La ficha de Nexo (etapa 4, «Nexo adopta las piezas»): todo sale de nexo.hallazgos, por public.nexo_viz_hallazgos, en el mismo orden
 * y con las mismas piezas que la ficha de Argus Team (diseño 4a §3): estado en la lista, la respuesta, las dos preguntas, qué hacer, el
 * dibujo con la línea de tiempo y el porqué, lo que no sabemos, el detalle cerrado y el alcance. No redacta: pone rótulos del catálogo
 * (catalogo.ts y el de las piezas) y nunca concluye.
 *
 * Diferencias con Argus Team, por lo que Nexo no tiene: datos del cliente para comparar (el buscador de Nexo no los pide, así que no se
 * muestra la comparación ni el texto 4, que mandaría a escribirlos) y decisiones del equipo sobre la identidad (texto 37).
 */

/** Cuánto se espera antes de decir «Sigue cargando…» (diseño §2.13). */
export const LENTO_MS = 3000;

type Estado =
  | { estado: "cargando" }
  | { estado: "lista"; doc: Hallazgos }
  | { estado: "error"; razon: string; mensaje: string };

interface Props {
  cliente: ClienteNexo;
  sujeto: SujetoFicha;
  /** «Buscar de nuevo» (textos 46 y 47): la corrida cambió o la persona ya no está. */
  onBuscarDeNuevo?(): void;
  /** En la página de una entidad el nombre ya está arriba; la ficha no lo repite. */
  mostrarNombre?: boolean;
  /** Avisa si la ficha se pudo pintar («lista») o no («error»): la página de una entidad oculta lo viejo sólo si se pintó. */
  onEstado?(estado: "cargando" | "lista" | "error"): void;
}

export function FichaHallazgos({ cliente, sujeto, onBuscarDeNuevo, mostrarNombre = true, onEstado }: Props) {
  const [estado, setEstado] = useState<Estado>({ estado: "cargando" });
  const [lento, setLento] = useState(false);
  const [intento, setIntento] = useState(0);
  const clave = JSON.stringify(sujeto);
  const avisar = useRef(onEstado);
  avisar.current = onEstado;
  useEffect(() => { avisar.current?.(estado.estado); }, [estado.estado]);

  useEffect(() => {
    // La respuesta tardía de otra persona o empresa se descarta y su petición se cancela (diseño §2.13).
    const control = new AbortController();
    setEstado({ estado: "cargando" });
    setLento(false);
    const reloj = setTimeout(() => { setLento(true); }, LENTO_MS);
    vizHallazgos(cliente, JSON.parse(clave) as SujetoFicha, control.signal)
      .then(
        (r) => {
          if (control.signal.aborted) return;
          const doc = leerHallazgos(r);
          setEstado(doc ? { estado: "lista", doc } : { estado: "error", razon: "ilegible", mensaje: FICHA.ilegible });
        },
        (e: unknown) => {
          if (control.signal.aborted || esCancelacion(e)) return;
          const razon = razonDe(e);
          setEstado({ estado: "error", razon, mensaje: mensajeDeError(razon) });
        },
      )
      .finally(() => { clearTimeout(reloj); });
    return () => {
      control.abort();
      clearTimeout(reloj);
    };
  }, [cliente, clave, intento]);

  if (estado.estado === "cargando") {
    return (
      <div className="nx-ficha" aria-busy="true">
        <p className="nx-tenue" role="status">{lento ? FICHA.sigueCargando : FICHA.cargando}</p>
        <div className="nx-esqueleto" />
        <div className="nx-esqueleto" />
      </div>
    );
  }
  if (estado.estado === "error") {
    const accion = accionDeError(estado.razon);
    return (
      <div className="nx-ficha">
        <p className="nx-error" role="alert">{estado.mensaje}{" "}
          {accion === "buscar" && onBuscarDeNuevo ? <button type="button" className="nx-btn" onClick={onBuscarDeNuevo}>{FICHA.buscarDeNuevo}</button> : null}
          {accion === "reintentar" ? <button type="button" className="nx-btn" onClick={() => { setIntento((n) => n + 1); }}>{FICHA.reintentar}</button> : null}
        </p>
      </div>
    );
  }
  return <Documento cliente={cliente} sujeto={sujeto} doc={estado.doc} mostrarNombre={mostrarNombre} {...(onBuscarDeNuevo ? { onBuscarDeNuevo } : {})} />;
}

/** Una fuente abierta en el visor, con la frase de «También» cuando la hay. */
interface Abierta { fuente: FuenteAbrible; frase: string | null }

function Documento({ cliente, sujeto, doc, mostrarNombre, onBuscarDeNuevo }: {
  cliente: ClienteNexo; sujeto: SujetoFicha; doc: Hallazgos; mostrarNombre: boolean; onBuscarDeNuevo?(): void;
}) {
  const [abierta, setAbierta] = useState<Abierta | null>(null);
  // Al cerrar el visor, el foco vuelve a lo que lo abrió.
  const abridor = useRef<HTMLElement | null>(null);
  const abrir = useCallback((f: Fuente | null | undefined, sancionado: string | null = null, frase: string | null = null) => {
    if (!f) return;
    const fuente = abrible(f, contextoDe(doc, sancionado));
    // Corte 0: una fuente de un tipo que esta pantalla no conoce no se abre (ni un visor vacío).
    if (!seAbre(fuente)) return;
    const activo = document.activeElement;
    abridor.current = activo instanceof HTMLElement || activo instanceof SVGElement ? (activo as HTMLElement) : null;
    setAbierta({ fuente, frase });
  }, [doc]);
  const cerrar = () => {
    setAbierta(null);
    const a = abridor.current;
    abridor.current = null;
    if (a && a.isConnected) a.focus();
  };
  /** «Ver la fuente», con un nombre accesible que dice de quién (empieza con el texto visible). */
  const verFuente = (f: Fuente | null | undefined, de: string, sancionado: string | null = null) => f && seAbre(abrible(f, contextoDe(doc, sancionado)))
    ? <button type="button" className="nx-liga" aria-label={`${FICHA.verFuente}: ${de}`} onClick={() => { abrir(f, sancionado); }}>{FICHA.verFuente}</button>
    : null;

  const sinHallazgos = doc.nivel.valor === "sin_hallazgos";
  const grupos = porContraparte(doc);
  const masPersonas = textoMasPersonas(doc);
  const masHuecos = textoMasHuecos(doc);
  const voces = vocesDelCamino(doc);
  const argus = doc.hallazgos.some((h) => h.frases.some((f) => f.voz === "argus"))
    || doc.hallazgos.some((h) => h.relacion.a_traves_de.some((p) => p.en_lista.estado === "cuenta_como_sancionada"))
    || doc.en_lista.estado === "cuenta_como_sancionada";
  // La fuente de un elemento del dibujo: el índice en dibujo.fuentes y, para lo de la lista de OFAC, el sancionado que el propio
  // elemento dice (dos sancionados pueden compartir la misma fuente).
  const desdeDibujo = (i: number, d: Dibujo, e: Elemento) => {
    const f = d.fuentes[i];
    if (!f) return;
    abrir(f, "sancionado" in e && e.sancionado ? e.sancionado : null);
  };
  const deQuien = (e: Elemento): string | null => {
    const ref = "sancionado" in e ? e.sancionado : undefined;
    const nombre = ref ? doc.sancionados[ref]?.nombre ?? (ref === doc.sujeto.ref ? doc.sujeto.nombre : null) : null;
    return nombre ?? (e.tipo === "texto" ? e.completo : null);
  };
  const dibujarCamino = useCallback((ancho: number) => camino(doc, { ancho }), [doc]);
  const dibujarTiempo = useCallback((ancho: number) => lineaDeTiempo(doc, { ancho }), [doc]);
  const tiempo = lineaDeTiempo(doc, { ancho: 520 });
  const hayRayado = (tiempo?.elementos ?? []).some((e) => e.tipo === "rayado");
  const nombreDe = (ref: string) => doc.sancionados[ref]?.nombre ?? doc.sujeto.nombre;
  const tesoro = [...new Set(grupos.map((g) => g.ref))].map((ref) => loQueDiceElTesoro(doc, ref)).filter((b) => b !== null);
  const actas = actasDelDocumento(doc);
  const avisos = avisosOmitidos(doc);
  const identidades = sinHallazgos ? [] : grupos.flatMap((g) => {
    const id = g.hallazgos.find((h) => h.contraparte.identidad)?.contraparte.identidad;
    return id ? [{ ref: g.ref, nombre: g.nombre, texto: id.texto }] : [];
  });
  const conProgramas = Object.entries(doc.sancionados).flatMap(([ref, s]) => (s && s.en_lista.programas.length > 0 ? [{ ref, s }] : []));
  // Corte 2 de los certificados: una contraparte que sólo se relaciona por certificados del SAT no tiene actas que pedir.
  const conActos = sinHallazgos ? [] : grupos.filter((g) => uuidDe(g.ref) && conActas(g));
  // El detalle sólo aparece si tiene algo que mostrar: nunca «Detalle: 0 actas» ni un detalle vacío.
  const hayDetalle = identidades.length > 0 || conProgramas.length > 0 || avisos.length > 0 || !!masHuecos || !!masPersonas || conActos.length > 0;
  const tono = tonoNivel(doc.nivel.valor);
  // La primera pregunta, sin comparación con el cliente (Nexo no pide sus datos): lo que el documento dice de la identidad.
  const primera = textosPrimeraPregunta(doc);
  const pasos = pasosQueHacer(doc);

  return (
    <article className="nx-ficha" aria-label={doc.sujeto.nombre}>
      {abierta ? (
        <VerFuente cliente={cliente} fuente={abierta.fuente} doc={doc} frase={abierta.frase} onCerrar={cerrar} {...(onBuscarDeNuevo ? { onBuscarDeNuevo } : {})} />
      ) : null}

      {/* 1. Estado en la lista */}
      <header className="nx-ficha-cabeza" data-parte="estado">
        {mostrarNombre ? <h2>{doc.sujeto.nombre}</h2> : null}
        <p className="nx-ficha-estado"><b>{doc.en_lista.texto}</b></p>
        <p className="nx-tenue">
          {doc.cortes.lista_ofac ? texto(FICHA.listaConsultada, { fecha: fechaLlana(doc.cortes.lista_ofac) }) : FICHA.listaSinFecha}
          {doc.cortes.registro?.corte_ofac ? <>{" "}{texto(FICHA.relacionesCalculadas, { fecha: fechaLlana(doc.cortes.registro.corte_ofac) })}</> : null}
        </p>
        {/* Corte 4a de los certificados: quien sólo aparece en los certificados del SAT, y lo que todavía no se puede (aprobado) */}
        {esSatRep(doc.sujeto.ref) ? <p className="nx-tenue" data-aviso="sat-rep">{avisoSatRep(doc)}</p> : null}
      </header>

      {/* 2. La respuesta */}
      <section data-parte="respuesta">
        <p className="nx-respuesta">{doc.nivel.porque}</p>
        {doc.nivel.ajustes.map((a, i) => <p key={i} className="nx-tenue">{a.texto}</p>)}
      </section>

      {/* 3. Las dos preguntas */}
      <div className="nx-dos-preguntas" data-parte="preguntas">
        {primera.length > 0 || masPersonas ? (
          <section className="nx-caja">
            <h3 className="nx-rotulo">{doc.sujeto.tipo === "empresa" ? FICHA.mismaEmpresa : FICHA.mismaPersona}</h3>
            {primera.map((t, i) => <p key={i}>{t}</p>)}
            {masPersonas ? <p className="nx-tenue">{masPersonas}</p> : null}
          </section>
        ) : null}
        <section className={`nx-caja nx-nivel-${tono}`} data-nivel={doc.nivel.valor}>
          <h3 className="nx-rotulo">{FICHA.siEsTuCliente}</h3>
          <p className="nx-grande">{doc.nivel.etiqueta}</p>
          {doc.nivel.importa ? <p>{doc.nivel.importa}</p> : null}
        </section>
      </div>

      {/* 4. Qué hacer */}
      {pasos.length > 0 ? (
        <section className="nx-caja nx-quieta" data-parte="que-hacer">
          <h3 className="nx-rotulo">{FICHA.queHacer}</h3>
          <ol className="nx-pasos">{pasos.map((q, i) => <li key={i}>{q}</li>)}</ol>
        </section>
      ) : null}

      {/* 5. El dibujo, la línea de tiempo y el porqué */}
      {!sinHallazgos && doc.hallazgos.length > 0 ? (
        <section className="nx-caja" data-parte="camino">
          <div className="nx-pieza-cabeza"><h3 className="nx-rotulo">{FICHA.comoSeRelaciona}</h3>{voces ? <span className="nx-tenue">{voces}</span> : null}</div>
          <Pieza dibujar={dibujarCamino} onFuente={desdeDibujo} deQuien={deQuien} />
          <p className="nx-leyenda">
            <span><i className="nx-ley-doc" aria-hidden="true" />{ROTULOS.leyendaDocumento}</span>
            {argus ? <span><i className="nx-ley-argus" aria-hidden="true" />{ROTULOS.leyendaArgus}</span> : null}
          </p>
          {avisos.map((a, i) => <p key={i} className="nx-tenue">{a}</p>)}
          <ul className="nx-frases">
            {grupos.map((g) => (
              <li key={g.ref}>
                <b>{g.nombre}</b>
                <ul>
                  {g.hallazgos.flatMap((h) => h.frases.map((f, i) => (
                    <li key={`${h.id}-${String(i)}`}>
                      {f.voz === "argus" ? <span className="nx-marca-argus">{ROTULOS.leyendaArgus}</span> : null}{" "}
                      {f.texto}{" "}{verFuente(f.fuente, g.nombre, f.voz === "lista_ofac" ? h.contraparte.ref : null)}
                      {fuenteSinCopia(f.fuente) ? <span className="nx-tenue"> · {fuenteSinCopia(f.fuente)}</span> : null}
                    </li>
                  )))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {!sinHallazgos && tiempo ? (
        <section className="nx-caja" data-parte="tiempo">
          <div className="nx-pieza-cabeza"><h3 className="nx-rotulo">{ROTULOS.tituloTiempo}</h3><span className="nx-tenue">{subtituloTiempo(doc)}</span></div>
          <Pieza dibujar={dibujarTiempo} onFuente={desdeDibujo} deQuien={deQuien} />
          {hayRayado ? <p className="nx-leyenda"><span><i className="nx-ley-no-sabemos" aria-hidden="true" />{ROTULOS.leyendaNoSabemos}</span></p> : null}
        </section>
      ) : null}
      {doc.huecos.length > 0 || masHuecos ? (
        <section className="nx-caja" data-parte="huecos">
          <h3 className="nx-rotulo">{FICHA.loQueNoSabemos}</h3>
          <ul className="nx-huecos">
            {doc.huecos.map((q) => (
              <li key={q.id}>
                <p>{q.texto}</p>
                {q.si_se_confirma_etiqueta ? <p className="nx-tenue">{texto(FICHA.siSeConfirma, { nivel: q.si_se_confirma_etiqueta })}</p> : null}
                {q.cierra ? (
                  <p className="nx-tenue"><b>{q.cierra.a === "cliente" ? FICHA.pideCliente : q.cierra.a === "area_cuentas" ? FICHA.pideCuentas : FICHA.pideAnalista}</b> {q.cierra.pide}</p>
                ) : null}
              </li>
            ))}
          </ul>
          {masHuecos ? <p className="nx-tenue">{masHuecos}</p> : null}
        </section>
      ) : null}
      {/* Corte 2 de los certificados del SAT: «Ante el SAT», después de «Lo que no sabemos» (diseño §9). Contexto: no cambia el nivel. */}
      <AnteElSat cliente={cliente} sujeto={sujeto} doc={doc} {...(onBuscarDeNuevo ? { onBuscarDeNuevo } : {})} />
      {!sinHallazgos ? tesoro.map((b) => (
        <section key={b.ref} className="nx-caja" data-parte="tesoro">
          <h3 className="nx-rotulo">{b.titulo}</h3>
          <p>{b.porque}</p>
          <p><b>{b.significa.rotulo}</b> {b.significa.texto}</p>
          {b.historia.length > 0 ? (
            <ul className="nx-historia">{b.historia.map((x, i) => (
              <li key={i}>{x.texto}{" "}{x.fuente !== null ? verFuente(b.fuentes[x.fuente]?.fuente, nombreDe(b.ref), b.ref) : null}</li>
            ))}</ul>
          ) : null}
          {b.cita ? (
            <figure className="nx-cita">
              <blockquote>{b.cita.traduccion}</blockquote>
              <figcaption className="nx-tenue">{b.cita.voz}{" "}{verFuente(b.fuentes[b.cita.fuente]?.fuente, nombreDe(b.ref), b.ref)}</figcaption>
              <details><summary>{b.cita.leerOriginal}</summary><blockquote lang="en">{b.cita.original}</blockquote></details>
            </figure>
          ) : null}
          {b.organigramas.length > 0 ? (
            <p>{b.organigramas.map((i) => <span key={i}>{verFuente(b.fuentes[i]?.fuente, nombreDe(b.ref), b.ref)}{" "}</span>)}</p>
          ) : null}
        </section>
      )) : null}
      {doc.tambien.length > 0 ? (
        <section className="nx-caja nx-quieta" data-parte="tambien">
          <h3 className="nx-rotulo">{FICHA.otrasListas}</h3>
          <ul>{doc.tambien.map((t, i) => (
            <li key={i}>{t.texto}{" "}
              <button type="button" className="nx-liga" aria-label={`${FICHA.verFuente}: ${doc.sujeto.nombre}`} onClick={() => { abrir(t.fuente, null, t.texto); }}>{FICHA.verFuente}</button>
            </li>
          ))}</ul>
        </section>
      ) : null}

      {/* 6. El detalle, cerrado */}
      {hayDetalle ? (
        <details className="nx-detalle" data-parte="detalle">
          <summary>{actas === 0 ? FICHA.detalleSinActas : actas === 1 ? FICHA.detalleUna : texto(FICHA.detalleVarias, { n: actas })}</summary>
          <div className="nx-detalle-cuerpo">
            {identidades.map((x) => <p key={x.ref}><b>{x.nombre}</b> {x.texto}</p>)}
            {conProgramas.length > 0 ? (
              <div>
                <h4>{FICHA.programas}</h4>
                <ul>{conProgramas.map(({ ref, s }) => s.en_lista.programas.map((p) => <li key={`${ref}-${p.clave}`}>{s.nombre}: {p.clave} · {p.texto}</li>))}</ul>
              </div>
            ) : null}
            {avisos.map((a, i) => <p key={i} className="nx-tenue">{a}</p>)}
            {masHuecos ? <p className="nx-tenue">{masHuecos}</p> : null}
            {masPersonas ? <p className="nx-tenue">{masPersonas}</p> : null}
            {conActos.map((g) => <ActosCon key={g.ref} cliente={cliente} sujeto={sujeto} contraparte={g.ref} nombre={g.nombre} onFuente={(f) => { abrir(f); }} />)}
          </div>
        </details>
      ) : null}

      {/* 7. El alcance */}
      <section className="nx-ficha-alcance" data-parte="alcance">
        <h3 className="nx-rotulo">{FICHA.queEvalua}</h3>
        <p className="nx-tenue">{doc.alcance}</p>
      </section>
    </article>
  );
}

/** «Relaciones con [nombre] en las actas» (texto 32): la lista de actos de nexo.hallazgo_detalle, al pedirla (corte 4a, §2.13). */
function ActosCon({ cliente, sujeto, contraparte, nombre, onFuente }: {
  cliente: ClienteNexo; sujeto: SujetoFicha; contraparte: string; nombre: string; onFuente(f: Fuente): void;
}) {
  const [estado, setEstado] = useState<"cerrado" | "cargando" | ActoDetalle[] | { error: string }>("cerrado");
  const control = useRef<AbortController | null>(null);
  useEffect(() => () => { control.current?.abort(); }, []);
  const pedir = () => {
    if (!uuidDe(contraparte)) return;
    control.current?.abort();
    const c = new AbortController();
    control.current = c;
    setEstado("cargando");
    vizHallazgoDetalle(cliente, sujeto, contraparte, c.signal).then(
      (r) => { if (!c.signal.aborted) setEstado(r.actos); },
      (e: unknown) => { if (!c.signal.aborted && !esCancelacion(e)) setEstado({ error: mensajeDeError(razonDe(e)) }); },
    );
  };
  const titulo = texto(FICHA.relacionesCon, { nombre });
  if (estado === "cerrado") return <p><button type="button" className="nx-btn" onClick={pedir}>{titulo}</button></p>;
  if (estado === "cargando") return <div className="nx-esqueleto" aria-busy="true" />;
  if (!Array.isArray(estado)) {
    return <p className="nx-error" role="alert">{estado.error} <button type="button" className="nx-btn" onClick={pedir}>{FICHA.reintentar}</button></p>;
  }
  return (
    <div className="nx-actos">
      <h4>{titulo}</h4>
      <ul>{estado.map((a, i) => {
        const f = esFuente(a.fuente) ? a.fuente : null;
        return (
          <li key={i}>
            {a.papel_texto ? <b>{a.papel_texto}</b> : null}
            {a.fecha ? <> · {FICHA.fechaActa}: {fechaLlana(a.fecha)}</> : null}
            {a.acciones !== null ? (
              <> · {a.total_acciones !== null
                ? texto(FICHA.accionesDe, { n: numeroLlano(a.acciones), m: numeroLlano(a.total_acciones) })
                : texto(ROTULOS.accionesSinTotal, { n: numeroLlano(a.acciones) })}</>
            ) : null}
            {f && seAbre(abrible(f, { corrida: null })) ? <>{" "}<button type="button" className="nx-liga" aria-label={`${FICHA.verFuente}: ${nombre}`} onClick={() => { onFuente(f); }}>{FICHA.verFuente}</button></> : null}
          </li>
        );
      })}</ul>
    </div>
  );
}
const esFuente = (x: unknown): x is Fuente => typeof x === "object" && x !== null && typeof (x as { tipo?: unknown }).tipo === "string";
